// Progress bar under the Runtime Status box. There's no reliable time estimate (a first score takes ~1-2
// min, a cached re-score is near-instant), so this is the indeterminate "trickle": it eases toward a
// per-phase ceiling at a slightly random speed and NEVER reaches the end during scoring, then snaps to
// 100% the moment the task finishes (phase 'done'). Drives both toolbars' bars (Quick Snapshot's
// #sk-pbar and the shared One-Off #callbar-pbar) at once.
//
// Lifecycle: a new import restarts it (phase 'importing'); Stop freezes it where it is; Clear / a failed
// import empties + hides it. Navigating between lens pages doesn't touch it (the bars live in the pinned
// toolbars).

const CEIL = { importing: 22, imported: 34, running: 99 };
const RUN_CAP = 99.2; // during a live phase the bar creeps toward, but never reaches, this

let current = 0;
let ceiling = 0;
let frozen = false;
let timer: ReturnType<typeof setInterval> | undefined;

const byId = (ids: string[]): HTMLElement[] => ids.map((id) => document.getElementById(id)).filter((n): n is HTMLElement => !!n);
const fills = (): HTMLElement[] => byId(['sk-pbar-fill', 'callbar-pbar-fill']);
const bars = (): HTMLElement[] => byId(['sk-pbar', 'callbar-pbar']);

function paint(): void {
  for (const f of fills()) f.style.width = current.toFixed(1) + '%';
}
function show(on: boolean): void {
  for (const b of bars()) b.classList.toggle('is-active', on);
}
function stopTimer(): void {
  if (timer) clearInterval(timer);
  timer = undefined;
}
function tick(): void {
  if (frozen) {
    stopTimer();
    return;
  }
  if (ceiling >= 100) {
    // Task done → ease quickly to 100 and stop.
    current = Math.min(current + (100 - current) * 0.3 + 0.6, 100);
    paint();
    if (current >= 99.9) {
      current = 100;
      paint();
      stopTimer();
    }
    return;
  }
  // Live phase: a staged trickle — fast early, ever slower near the top, but the step is NEVER zero, so
  // it keeps visibly creeping through a long score (no parking at a fixed ceiling) while never reaching
  // the end. Only stops once it's a hair under 100.
  const cap = Math.min(ceiling, RUN_CAP);
  if (current >= cap) {
    stopTimer();
    return;
  }
  let inc: number;
  if (current < 45) inc = 0.7 + Math.random() * 1.0;
  else if (current < 75) inc = 0.3 + Math.random() * 0.5;
  else if (current < 90) inc = 0.12 + Math.random() * 0.2;
  else inc = 0.05 + Math.random() * 0.08; // 90 → 99: slow but always moving (~0.4-0.9%/s)
  current = Math.min(current + inc, cap);
  paint();
}
function run(): void {
  if (timer || frozen) return;
  timer = setInterval(tick, 140);
}
function raiseCeiling(c: number): void {
  if (frozen) return;
  ceiling = Math.max(ceiling, c); // never walk the bar backwards mid-task
  run();
}

/** Drive the bar off the scoring phase. Call from setPhase(). */
export function runtimeProgress(phase: string): void {
  switch (phase) {
    case 'importing': // a new import restarts the bar
      frozen = false;
      current = 0;
      ceiling = 0;
      paint();
      show(true);
      raiseCeiling(CEIL.importing);
      break;
    case 'imported':
      raiseCeiling(CEIL.imported);
      break;
    case 'running':
      raiseCeiling(CEIL.running);
      break;
    case 'done':
      frozen = false;
      raiseCeiling(100);
      break;
    case 'idle':
      // Failed import / normal reset empties it; but a Stop-frozen bar stays put until the next import.
      if (!frozen) resetRuntimeProgress();
      break;
    default:
      break;
  }
}

/** Stop clicked — freeze the bar where it is (until the next import). */
export function stopRuntimeProgress(): void {
  frozen = true;
  stopTimer();
}

/** Clear / reset — empty and hide the bar. */
export function resetRuntimeProgress(): void {
  frozen = false;
  stopTimer();
  current = 0;
  ceiling = 0;
  paint();
  show(false);
}
